import { useState } from 'react';
import clsx from 'clsx';
import { toggleOpFactuur, type OrderDetail } from '../../lib/orders';

interface OpFactuurToggleDialogProps {
  order: OrderDetail;
  isOpen: boolean;
  onClose: () => void;
  onToggled: () => void;
}

const MIN_REDEN_LENGTH = 10;

export function OpFactuurToggleDialog({
  order,
  isOpen,
  onClose,
  onToggled,
}: OpFactuurToggleDialogProps) {
  const currentlyOpFactuur = order.totals.opFactuur === true;
  const nextValue = !currentlyOpFactuur;

  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (reden.trim().length < MIN_REDEN_LENGTH) return;
    setSubmitting(true);
    setError(null);
    try {
      await toggleOpFactuur({
        werkOrderId: order.id,
        opFactuur: nextValue,
        reden: reden.trim(),
      });
      onToggled();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Op factuur-toggle mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  const title = nextValue
    ? 'Op factuur zetten'
    : 'Terug naar standaard betaal-flow';

  const submitLabel = nextValue
    ? 'Zet op factuur'
    : 'Terug naar standaard';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 mt-1">Ordernummer: {order.orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {nextValue ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
              <p className="font-semibold">Let op — klantcommunicatie verandert:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Geen iDEAL/pin/contant-mention in opdrachtbevestiging</li>
                <li>Geen Mollie payment-link in factuur-mail</li>
                <li>Geen automatische betaalherinneringen (7d / 15d / 30d / 37d)</li>
                <li>Betaaltermijn op factuur: 14 dagen</li>
              </ul>
            </div>
          ) : (
            <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              Klant ontvangt weer iDEAL/pin/contant-communicatie en
              automatische betaalherinneringen.
            </div>
          )}

          <label className="block">
            <span className="text-sm text-slate-700">Reden (verplicht, min. 10 tekens)</span>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder={
                nextValue
                  ? 'Bv. klant vraagt expliciet om factuur achteraf — heeft geen iDEAL'
                  : 'Bv. klant heeft alsnog gekozen voor iDEAL betaling'
              }
              disabled={submitting}
            />
            <span className="block mt-1 text-xs text-slate-400">
              {reden.trim().length} / {MIN_REDEN_LENGTH}+ tekens
            </span>
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || reden.trim().length < MIN_REDEN_LENGTH}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || reden.trim().length < MIN_REDEN_LENGTH
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Bezig…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
